import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Megaphone, PenTool, Target } from 'lucide-react';
import { Item, Stagger } from './Reveal';

// Adapted from Watermelon UI block "feature-3" (copy + checklist beside floating insight cards).
const highlights = [
  'Strategies tailored to businesses of every size',
  'Built on the latest digital marketing trends and technologies',
  'Personal care from a dedicated team of professionals',
  'Clear reporting on the results that matter to you',
];

const services = [
  {
    icon: Target,
    title: 'Digital Strategy',
    text: 'A bespoke roadmap shaped around your goals, audience and market — the foundation for everything we do together.',
  },
  {
    icon: Megaphone,
    title: 'Online Advertising',
    text: 'Campaigns across search, social and display, planned and managed to put your business in front of the right people.',
  },
  {
    icon: PenTool,
    title: 'Brand & Content',
    text: 'Messaging and creative that make your brand recognisable and give your audience a reason to choose you.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Optimisation',
    text: 'Continuous measurement and refinement, so every campaign keeps improving and every decision is backed by data.',
  },
];

function FloatingCard({ className = '', children }) {
  return (
    <div className={`absolute rounded-lg bg-paper-2/90 p-4 shadow-float ring-1 ring-black/5 backdrop-blur-md ${className}`}>
      {children}
    </div>
  );
}

export default function Services() {
  return (
    <section id="services" className="bg-paper py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Stagger className="space-y-6">
            <Item as="p" className="eyebrow">Our services</Item>
            <Item as="h2" className="text-4xl font-normal leading-[1.05] tracking-[-0.045em] text-balance sm:text-5xl lg:text-[3.6rem]">
              Your gateway to a world of{' '}
              <span className="font-serif italic tracking-[-0.02em] text-sage-deep">endless possibilities</span>
            </Item>
            <Item as="p" className="max-w-lg text-[1.02rem] leading-relaxed text-ink-soft">
              Explore a carefully curated set of services designed to grow your business and exceed your
              expectations. Experience innovation, excellence and personalised care — and let&rsquo;s create
              something extraordinary together.
            </Item>
            <Item as="ul" className="space-y-3 pt-1">
              {highlights.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-sage/15 shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]">
                    <span className="size-2.5 rounded-full bg-sage" />
                  </span>
                  <span className="text-[0.95rem] text-ink-soft">{item}</span>
                </li>
              ))}
            </Item>
            <Item className="pt-3">
              <Link to="/#contact" className="btn-primary group">
                Get started
                <ArrowRight className="size-4 -rotate-45 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </Item>
          </Stagger>

          <Stagger amount={0.3}>
            <Item className="relative h-[460px] overflow-hidden rounded-xl shadow-[inset_0_0_4px_rgba(0,0,0,0.1)] sm:h-[520px]">
              <img
                src="/images/desk.jpg"
                alt="A smartphone showing a clean website, beside a succulent and iced coffee"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />

              <FloatingCard className="left-4 top-5 w-[230px] sm:left-6 sm:top-6 sm:w-[250px]">
                <p className="text-xs text-muted">Strategy</p>
                <p className="mt-1 text-lg font-semibold tracking-tight">Built around your goals</p>
                <div className="mt-3 flex gap-2 text-[10px] font-medium">
                  <span className="rounded-md bg-sage/15 px-2 py-0.5 text-sage-deep">Research</span>
                  <span className="rounded-md bg-amber/25 px-2 py-0.5 text-[#8A5A16]">Positioning</span>
                </div>
              </FloatingCard>

              <FloatingCard className="bottom-24 right-4 z-10 w-[230px] sm:right-6 sm:w-[250px]">
                <p className="text-xs text-muted">Channel mix</p>
                <p className="mt-1 text-sm text-muted">
                  <span className="font-medium text-ink">One plan,</span> every touchpoint
                </p>
                <div className="mt-3 flex h-2 w-full gap-1">
                  <div className="w-[40%] rounded-full bg-sage" />
                  <div className="w-[35%] rounded-full bg-forest" />
                  <div className="w-[25%] rounded-full bg-amber" />
                </div>
                <div className="mt-2.5 flex gap-3 text-[10px] text-muted">
                  <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-sage" />Search</span>
                  <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-forest" />Social</span>
                  <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-amber" />Display</span>
                </div>
              </FloatingCard>

              <FloatingCard className="bottom-5 left-6 w-[240px] sm:left-10">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Results, reviewed</span>
                  <span className="text-xs text-muted">Ongoing</span>
                </div>
                <div className="mt-3 flex gap-2 text-[10px] font-medium">
                  <span className="rounded-md bg-sage/15 px-2 py-0.5 text-sage-deep">Transparent</span>
                  <span className="rounded-md bg-ink/10 px-2 py-0.5 text-ink">Measurable</span>
                </div>
              </FloatingCard>
            </Item>
          </Stagger>
        </div>

        <Stagger className="mt-20 grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-2 lg:mt-28 lg:grid-cols-4">
          {services.map(({ icon: Icon, title, text }, i) => (
            <Item key={title} className="group flex flex-col bg-paper-2 p-7 transition-colors duration-300 hover:bg-white">
              <div className="flex items-center justify-between">
                <span className="inline-flex size-11 items-center justify-center rounded-lg bg-paper text-forest shadow-card transition-colors group-hover:bg-ink group-hover:text-paper">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                <span className="font-serif text-xl italic text-muted/70">0{i + 1}</span>
              </div>
              <h3 className="mt-8 text-xl font-medium tracking-[-0.03em]">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-soft">{text}</p>
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
